<template>
  <section class="biography">
    <transition name="fade" appear>
      <h2>Our Story</h2>
    </transition>
    <div class="bio-text load-hidden">
      <p class="bio-para load-hidden">
        <span class="highlight" title="Melodic Heavy Metal band from Greece"
          >NightKill</span
        >
        — though still unnamed at the time — began in Athens, Greece, as a
        creative project between
        <span class="name" title="Lead Guitar">Panos Pavlou</span> and
        <span class="name" title="Bass Guitar">Peter Jonathan Parker</span>,
        fueled by their passion for powerful melodies and heavy riffs. In the
        summer of 2023,
        <span class="name" title="Drums">Zois Psarakis</span> joined the
        project, laying down a solid rhythmic backbone. Shortly after, in
        September,
        <span class="name" title="Lead Guitar">Giorgos Mylonas</span> stepped in
        as second lead guitarist, bringing harmonic precision and energy. The
        lineup was completed in November with the arrival of
        <span class="name" title="Vocals">Theodor Martinis</span>, locking in
        the final piece of the puzzle.
      </p>

      <p class="bio-para load-hidden">
        With the full formation in place, the band chose the name
        <span class="highlight" title="The band's official name"
          >NightKill</span
        >
        and immediately dove into writing, and soon after, recording their debut
        album,
        <span class="highlight" title="Debut album released 2025"
          >Survive the Night</span
        >.
      </p>

      <p class="bio-para load-hidden">
        <span class="highlight" title="Greek melodic heavy metal band"
          >NightKill</span
        >’s sound is shaped by the legacy of ‘80s heavy metal, drawing
        inspiration from icons like Queen, Scorpions, Dokken, Dio, and Judas
        Priest — blending timeless influence with a fresh, modern edge.
      </p>
    </div>

    <transition name="modal-fade">
      <PhotoAlbum
        v-if="selectedUser"
        :member="selectedUser"
        @close="selectedUser = null"
        @open-photo="openPhotoViewer"
      ></PhotoAlbum>
    </transition>

    <transition name="zoom-move-fade" appear>
      <PhotoViewer
        v-if="selectedPhotoPath"
        :photo="selectedPhotoPath"
        @close="selectedPhotoPath = null"
      ></PhotoViewer>
    </transition>

    <div class="lineup">
      <h2 class="load-hidden">Meet the Band</h2>

      <div class="members">
        <div
          v-for="member in bandMembers"
          :key="member.name"
          class="member load-hidden"
        >
          <img
            :src="member.photo"
            :alt="member.name"
            class="member-photo"
            @click="focus(member)"
          />
          <h3 class="member-name">{{ member.name }}</h3>
          <p class="member-role">{{ member.role }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ScrollReveal from 'scrollreveal';
import PhotoViewer from '@/components/media/PhotoViewer.vue';
import PhotoAlbum from '@/components/members/PhotoAlbum.vue';
import { useHead } from '@vueuse/head';

export default {
  name: 'BandView',

  components: { PhotoAlbum, PhotoViewer },
  data() {
    return {
      bandMembers: [
        {
          name: 'Theodor Martinis',
          role: 'Vocals',
          photo: '/media/photos/band/Theodor.webp',
          albumName: 'theodor',
        },
        {
          name: 'Panos Pavlou',
          role: 'Lead Guitar',
          photo: '/media/photos/band/Panos.webp',
          albumName: 'panos',
        },
        {
          name: 'Giorgos Mylonas',
          role: 'Lead Guitar',
          photo: '/media/photos/band/Giorgos.webp',
          albumName: 'giorgos',
        },
        {
          name: 'Peter Jonathan Parker',
          role: 'Bass',
          photo: '/media/photos/band/Peter.webp',
          albumName: 'peter',
        },
        {
          name: 'Zois Psarakis',
          role: 'Drums',
          photo: '/media/photos/band/Zois.webp',
          albumName: 'zois',
        },
      ],
      selectedUser: '',
      selectedPhotoPath: '',
    };
  },
  methods: {
    focus(member) {
      this.selectedUser = member;
    },
    openPhotoViewer(photoPath) {
      this.selectedPhotoPath = photoPath;
    },
  },

  mounted() {
    useHead({
      title: 'The Band | NightKill Official',
      meta: [
        {
          name: 'description',
          content:
            'Meet NightKill — the melodic heavy metal band from Greece. Discover the story, lineup, and musical roots.',
        },
        {
          name: 'keywords',
          content:
            'NightKill band biography, Greek metal band, heavy metal lineup, band members, NightKill origin story',
        },
        { property: 'og:title', content: 'The Band | NightKill Official' },
        {
          property: 'og:description',
          content:
            'NightKill is a melodic heavy metal band from Greece. Explore our roots, lineup, and metal inspirations.',
        },
        {
          property: 'og:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
        { property: 'og:url', content: 'https://nightkill.gr/band' },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: 'NightKill Official' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'The Band | NightKill Official' },
        {
          name: 'twitter:description',
          content:
            'Get to know NightKill — Greece’s melodic metal band. Lineup, story, and metal roots.',
        },
        {
          name: 'twitter:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
      ],
      link: [{ rel: 'canonical', href: 'https://nightkill.gr/band' }],
    });

    this.$nextTick(() => {
      setTimeout(() => {
        const sr = ScrollReveal({
          duration: 600,
          easing: 'ease-out',
          cleanup: true,
        });

        sr.reveal('.bio-para', {
          origin: 'bottom',
          distance: '30px',
          interval: 200,
        });

        sr.reveal('.lineup h2', {
          opacity: 0,
        });

        sr.reveal('.member', {
          origin: 'bottom',
          distance: '30px',
          delay: 200,
          interval: 150,
          scale: 0.9,
        });
      }, 100);
    });
  },
  beforeUnmount() {
    ScrollReveal().destroy();
  },
};
</script>

<style scoped>
p {
  text-align: justify;
}

.biography {
  padding: 0rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

.bio-text {
  margin-bottom: 4rem;
  text-align: center;
  /* opacity: 0; */
}

.bio-para {
  padding: 0 1rem;
}

h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
  margin-top: 0;
}

.lineup h2 {
  margin-bottom: 2rem;
}

.bio-text p {
  font-size: 1.2rem;
  line-height: 1.6;
  color: #ddd;
}

.members {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
}
.member {
  text-align: center;
  min-width: 230px;
  /* opacity: 0; */
}

.member-photo {
  width: 100%;
  max-width: 150px;
  border-radius: 25%;
  border: 3px solid #111;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.6);
  cursor: pointer;
}

.member-photo:hover {
  transform: scale(1.02);
  box-shadow: 0 0 10px #2c3e50;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.member-name {
  margin: 0.5rem 0 0;
  font-size: 1.25rem;
  font-weight: bold;
  color: #eee;
}

.member-role {
  color: #aaa;
  font-size: 1rem;
  text-align: center;
}

.name {
  font-weight: 600;
  color: #d8c49e; /* muted brass tone */
  cursor: help;
}

.highlight {
  font-weight: 700;
  color: #67667e; /* warm gold tone */
  cursor: help;
}

/* CSS for the fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Zoom + Move + Fade Transition */
.zoom-move-fade-enter-active,
.zoom-move-fade-leave-active {
  transition: all 0.45s ease;
}

.zoom-move-fade-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(30px);
}

.zoom-move-fade-enter-to {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.zoom-move-fade-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.zoom-move-fade-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(30px);
}

/* Fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.5s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
